import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { resolveEnrollmentEffectiveDate } from "@shared/lib/ageDetermination";

export interface SystemPlan {
  id: number;
  name: string;
  planCode?: string | null;
  type?: string | null;
  carrierId?: number | null;
  metalTier?: string | null;
  network?: string | null;
  visibleToClientId?: number | null;
  /** null = platform-curated ("Administr Library"); set = an agency's custom plan. */
  agencyId?: string | null;
  // Does this plan have rates usable in THIS browse context (the client being
  // browsed for, or shelf rates)? Set by GET /api/plans.
  hasRates?: boolean;
  // Plan-year window — AddPlanDialog checks the chosen link effective date
  // against it so the wrong plan-year version can't be linked.
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
  renewalDate?: string | Date | null;
}

/**
 * Searchable picker over system/library plans (clientId == null). Used by the
 * admin form builder (bind a checkbox to a specific library plan) and the
 * broker AddPlanDialog (match a manual plan to a library entry). Fetches via
 * GET /api/plans?scope=library.
 *
 * `clientId` scopes the browse context: plans marked private to a client only
 * appear when browsing for that client. `typeFilter` / `carrierIdFilter` /
 * `metalTierFilter` narrow the list client-side.
 */
export function SystemPlanCombobox({
  value,
  onSelect,
  typeFilter,
  carrierIdFilter,
  metalTierFilter,
  networkFilter,
  clientId,
  ratingAreaIdFilter,
  effectiveDateContains,
  wholeLibrary = false,
  placeholder = "Select system plan...",
  className,
  allowClear = true,
}: {
  value?: number | null;
  onSelect: (plan: SystemPlan | null) => void;
  /** Restrict to these plan types (e.g. ["health", "medical"]). */
  typeFilter?: string[];
  /** Restrict to one carrier. */
  carrierIdFilter?: number | null;
  /** Restrict to one metal tier (e.g. "Gold"). */
  metalTierFilter?: string | null;
  /** Restrict to one named network (e.g. "Select HMO"), case-insensitive. */
  networkFilter?: string | null;
  /** The client being browsed for — unlocks that client's private plans. */
  clientId?: number | null;
  /** Only offer plans priced for this rating area (or area-agnostic plans). */
  ratingAreaIdFilter?: number | null;
  /**
   * Only offer plans whose own plan year contains this date (YYYY-MM-DD) —
   * enrollment validation rejects dates outside the plan's year, so versions
   * for other years are noise. Dateless plans always pass.
   */
  effectiveDateContains?: string | null;
  /**
   * Superadmin tools only: every agency's custom plans too. Without it the
   * list is the Administr library plus the browsed client's own custom plans.
   */
  wholeLibrary?: boolean;
  placeholder?: string;
  className?: string;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const { data: plans = [], isLoading } = useQuery<SystemPlan[]>({
    queryKey: ["/api/plans", { scope: "library", clientId: clientId ?? null, ratingAreaId: ratingAreaIdFilter ?? null, wholeLibrary }],
    queryFn: async () => {
      const params = new URLSearchParams({ scope: "library" });
      if (clientId) params.set("clientId", String(clientId));
      if (wholeLibrary) params.set("wholeLibrary", "1");
      if (ratingAreaIdFilter != null) params.set("ratingAreaId", String(ratingAreaIdFilter));
      const res = await fetch(`/api/plans?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch library plans");
      return res.json();
    },
  });

  const filtered = plans.filter((p) => {
    if (typeFilter && !(p.type && typeFilter.includes(p.type))) return false;
    if (carrierIdFilter != null && p.carrierId !== carrierIdFilter) return false;
    if (
      metalTierFilter &&
      (p.metalTier || "").toLowerCase() !== metalTierFilter.toLowerCase()
    )
      return false;
    if (
      networkFilter &&
      (p.network || "").trim().toLowerCase() !== networkFilter.trim().toLowerCase()
    )
      return false;
    if (effectiveDateContains && (p as any).effectiveDate) {
      const resolved = resolveEnrollmentEffectiveDate(effectiveDateContains, p as any);
      if (resolved.tooEarly || resolved.tooLate) return false;
    }
    return true;
  });

  const selected = value != null ? plans.find((p) => p.id === value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className || "w-full text-sm"}`}
        >
          <span className="truncate">
            {selected
              ? `${selected.name}${selected.planCode ? ` (${selected.planCode})` : ""}`
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        {/* Literal substring matching — cmdk's default fuzzy filter makes
            "1000" match "Platinum 90 0/10" via scattered characters, which
            reads as broken search when hunting for a "Gold 1000" plan. */}
        <Command
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Search plans..." className="h-9" />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading plans..." : "No plans found."}
            </CommandEmpty>
            {allowClear && (
              <CommandItem
                value="__clear__"
                onSelect={() => {
                  onSelect(null);
                  setOpen(false);
                }}
              >
                <span className="text-gray-400">-- No plan --</span>
              </CommandItem>
            )}
            {/* Grouped sections: platform-curated plans vs custom (agency-built)
                ones. The server only returns the custom plans built for the
                client being browsed — an agency's custom plans don't carry
                over between clients (roadmap Phase 11) — unless wholeLibrary
                asks for all of them. */}
            {[
              { heading: "Administr Library", plans: filtered.filter((p) => p.agencyId == null) },
              { heading: "Custom plans", plans: filtered.filter((p) => p.agencyId != null) },
            ]
              .filter((section) => section.plans.length > 0)
              .map((section) => (
                <CommandGroup key={section.heading} heading={section.heading}>
                  {section.plans.map((p) => {
                    // Visible but unselectable — hiding it reads as broken search.
                    // Undefined (stale cache) must never disable.
                    const noRates = p.hasRates === false;
                    return (
                    <CommandItem
                      key={p.id}
                      value={`${p.name} ${p.planCode ?? ""} ${p.id}`}
                      disabled={noRates}
                      className={noRates ? "opacity-60" : undefined}
                      onSelect={() => {
                        if (noRates) return;
                        onSelect(p);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-3.5 w-3.5 ${value === p.id ? "opacity-100" : "opacity-0"}`}
                      />
                      <div className="flex flex-col">
                        <span className="truncate">
                          {p.name}
                          {p.network && (
                            <span className="ml-1 text-[11px] text-muted-foreground">· {p.network}</span>
                          )}
                        </span>
                        {p.planCode && (
                          <span className="text-[11px] text-muted-foreground font-mono">
                            {p.planCode}
                          </span>
                        )}
                        {noRates && (
                          <span className="text-[11px] text-amber-600 dark:text-amber-500">
                            {clientId ? "No rates for this client" : "No rates set up"}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
